import {
  BORROWER_INFO,
  CURRENT_POLICY,
  dateOfBirth,
  effectiveDate,
  firstName,
  lastName,
  mortgagee,
  phones,
  prefilled,
  premiumPayer,
  priorCoverages,
  priorPremium,
  propertyAddress,
} from "../industry.ts";
import type { Requirement } from "../requirements.ts";
import type { Carrier } from "./carrier.ts";

/** Steadily's landlord policy (DP3), as the recorded new quote of 2026-10-05 asked it. */
const REQUIREMENTS: Requirement[] = [
  firstName,
  lastName,
  {
    key: "ownerType",
    label: "Owned by",
    from: "answer",
    need: "provide",
    ask: "Is the property owned by a person or a company (such as an LLC)?",
  },
  {
    key: "businessName",
    label: "Company",
    from: "client",
    need: "provide",
    when: { key: "ownerType", equals: "company" },
    ask: "The name of the company that owns the property",
  },
  dateOfBirth,
  {
    key: "mailingAddress",
    label: "Owner's mailing address",
    from: "client",
    need: "provide",
    ask: "The owner's mailing address, if not the property",
  },
  {
    key: "emails",
    label: "Email",
    from: "client",
    need: "provide",
    ask: "The owner's email address",
    lookIn: BORROWER_INFO,
  },
  phones,
  propertyAddress,
  {
    key: "occupancy",
    label: "Occupancy",
    from: "property",
    need: "provide",
    ask: "How the property is used: rented out, seasonal, or owner-occupied with a tenant",
  },
  ...prefilled("units", "yearBuilt", "construction", "roofType", "squareFeet", "stories"),
  {
    key: "daysUninsured",
    label: "Days uninsured",
    from: "property",
    need: "provide",
    ask: "How many days the property has been without insurance (0 if none)",
  },
  priorPremium,
  {
    key: "shortTermRental",
    label: "Short-term rental",
    from: "answer",
    need: "provide",
    ask: "Is it rented for less than 3 months at a time, such as a vacation rental (yes or no)?",
  },
  {
    key: "propertyManager",
    label: "Third-party property manager",
    from: "answer",
    need: "provide",
    ask: "Does a third-party property manager look after it (yes or no)?",
  },
  {
    key: "centralAlarm",
    label: "Central station alarm",
    from: "answer",
    need: "provide",
    ask: "Does it have a central station fire or theft alarm (yes or no)?",
  },
  {
    key: "multiLocation",
    label: "Multi-location",
    from: "answer",
    need: "provide",
    ask: "Does the owner insure other properties with Steadily, or want several on this policy (yes or no)?",
  },
  effectiveDate,
  {
    ...premiumPayer,
    // The current policy's mortgage clause decides Steadily's payment schedule (CGM feedback 4).
    assume: { value: "insured", basis: "no mortgage on record: the insured pays in full" },
  },
  mortgagee,
  {
    key: "deductible",
    label: "All other perils deductible",
    from: "quote",
    need: "confirm",
    mirror: true,
    ask: "A deductible, if not Steadily's $2,500: $5,000, $10,000, or 1%, 2%, 3%, 5% or 10%",
    lookIn: CURRENT_POLICY,
  },
  {
    key: "dwellingCoverage",
    label: "Dwelling",
    from: "quote",
    need: "confirm",
    mirror: true,
    ask: "A dwelling limit, if not Steadily's estimate (it allows up to about 20% more)",
    lookIn: CURRENT_POLICY,
  },
  {
    key: "liabilityLimit",
    label: "Premises liability",
    from: "quote",
    need: "confirm",
    mirror: true,
    ask: "A liability limit, if not Steadily's $300,000",
    lookIn: CURRENT_POLICY,
  },
  priorCoverages,
];

/** Steadily as a carrier (the carrier layer): its portal and its questions. */
export const STEADILY: Carrier = {
  name: "Steadily",
  aliases: ["Steadily Insurance"],
  portalUrl: "https://app.steadily.com/login/",
  lines: "landlord, rental dwelling (DP3)",
  requirements: REQUIREMENTS,
};
